import {
  ActionButton,
  DefaultButton,
  Dialog,
  DialogFooter,
  Facepile,
  getTheme,
  IButtonStyles,
  IFacepileProps,
  IIconProps,
  IStackItemStyles,
  IStackStyles,
  PersonaSize,
  Stack,
  Text,
  Toggle
} from '@fluentui/react';
import * as React from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { RouteComponentProps, useHistory, withRouter } from 'react-router-dom';
import { QueueConfiguration } from '../../services';
import { AuthService } from '../../services/auth.service';
import { AdminContext } from '../../Store/Admin';
import { isDarkTheme, SalusThemeKey, WhiteStatic } from '../../theme';
import { SalusPrimaryButton } from '../shared/SalusPrimaryButton';
import { KeyboardShortcutModal } from './components/KeyboardShortcutModal';

const noop: () => undefined = () => undefined;

const signOutIcon: IIconProps = { iconName: 'SignOut' };
const shieldIcon: IIconProps = { iconName: 'Shield' };

export const SettingsLeavePromptMessage =
  'Your settings changes have not been saved. If you click "Yes", you will continue to dashboard.';
export const ReviewLeavePromptMessage =
  'Case has not been submitted. Leave the current case? If you click "Yes", the case will be placed back in the queue and you will continue to dashboard.';

export type NavBarProps = RouteComponentProps & {
  queues: QueueConfiguration[];
};

export const NavBar: React.FC<NavBarProps> = () => {
  const history = useHistory();
  const { state, dispatch } = React.useContext(AdminContext);

  const [user, setUser] = useState('');
  const [promptMessage, setPromptMessage] = useState('');
  const [togglingDarkMode, setTogglingDarkMode] = useState(false);
  const [authService, setAuthService] = useState<AuthService>();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const { fonts, palette } = getTheme();

  let logoButtonStyles: IButtonStyles = {
    icon: {
      fontSize: '2em'
    },
    label: {
      fontSize: '1.1em'
    },
    rootHovered: {
      backgroundColor: 'none'
    },
    rootPressed: {
      backgroundColor: 'none'
    },
    textContainer: {
      fontFamily: fonts.mediumPlus.fontFamily
    }
  };

  if (!isDarkTheme() && logoButtonStyles.textContainer) {
    logoButtonStyles = {
      ...logoButtonStyles,
      label: {
        color: palette.white,
        fontSize: '1.1em'
      },
      labelHovered: {
        backgroundColor: 'none',
        color: palette.themePrimary
      }
    };
  }

  const UserInfoStyles: IStackItemStyles = {
    root: {
      alignItems: 'center',
      display: 'flex'
    }
  };

  useEffect(() => {
    setAuthService(new AuthService());
  }, []);

  useEffect(() => {
    if (localStorage.getItem('UserName') !== null) {
      setUser(localStorage.getItem('UserName') || '');
    } else {
      setUser('Sign in');
    }
  }, []);

  const closeDialog = (closeFunc?: () => void): void => {
    setPromptMessage('');
    if (closeFunc) {
      closeFunc();
    }
  };

  const returnHome = () => {
    if (state?.tenantId) {
      dispatch({ type: 'CLEAR_QUEUE_CONFIG' });
      history.push(`/dashboard?tab=${state?.tenantId}`);
    } else {
      history.push('/');
    }
  };

  const stackStyles: IStackStyles = {
    root: {
      zIndex: 2,
      height: 50,
      background: isDarkTheme() ? palette.white : palette.black,
      color: isDarkTheme() ? palette.black : palette.white,
      boxShadow: '0 2px 2px -2px grey'
    }
  };

  const facepileProps: IFacepileProps = {
    personaSize: PersonaSize.size32,
    personas: [
      {
        imageInitials: user.charAt(0).toString(),
        tabIndex: -1,
        personaName: user,
        'aria-roledescription': 'Signed in user',
        // Facepile onClick needed to render persona with pointer events
        onClick: () => noop
      }
    ],
    getPersonaProps: () => {
      return {
        initialsTextColor: WhiteStatic,
        imageAlt: user,
        hidePersonaDetails: true
      };
    }
  };

  const toggleDarkMode = () => {
    setTogglingDarkMode(false);
    localStorage.setItem(SalusThemeKey, isDarkTheme() ? 'light' : 'dark');
    window.location.reload();
  };

  const toggleDarkModeWarning = () => {
    setTogglingDarkMode(true);
    if (history.location.pathname.startsWith('/review')) {
      setPromptMessage(ReviewLeavePromptMessage);
    } else {
      toggleDarkMode();
    }
  };

  return (
    <div>
      <header>
        <Stack>
          <Stack horizontal className="justify-content-between" styles={stackStyles}>
            <Stack.Item align="center">
              <Text variant="mediumPlus">
                <ActionButton
                  iconProps={shieldIcon}
                  title="Gaming Moderation Home"
                  ariaLabel="Gaming Moderation Home"
                  allowDisabledFocus
                  className="px-5"
                  text="Gaming Moderation"
                  styles={logoButtonStyles}
                  onClick={(): void => {
                    if (history.location.pathname.startsWith('/settings') && state.saveActive) {
                      setPromptMessage(SettingsLeavePromptMessage);
                    } else if (history.location.pathname.startsWith('/review')) {
                      setPromptMessage(ReviewLeavePromptMessage);
                    } else {
                      if (state?.tenantId) {
                        dispatch({ type: 'CLEAR_QUEUE_CONFIG' });
                        history.push(`/dashboard?tab=${state?.tenantId}`);
                      } else {
                        history.push('/');
                      }
                    }
                  }}
                />
              </Text>
            </Stack.Item>
            <Stack.Item styles={UserInfoStyles} align="center">
              <Stack.Item>
                <ActionButton
                  ariaLabel="Keyboard shortcut"
                  text="Keyboard shortcuts"
                  className="mr-3"
                  styles={logoButtonStyles}
                  onClick={() => {
                    setIsModalVisible(true);
                  }}
                />
                <KeyboardShortcutModal isModalVisible={isModalVisible} setIsModalVisible={setIsModalVisible} />
              </Stack.Item>
              <Toggle
                inlineLabel
                label="Dark Mode"
                styles={logoButtonStyles}
                style={{ marginTop: 4 }}
                className="mr-3 mt-1"
                onClick={toggleDarkModeWarning}
                checked={localStorage.getItem(SalusThemeKey) === 'dark'}
              />
              <Facepile styles={{ root: { padding: 10 } }} {...facepileProps} />
              <Text variant="mediumPlus">
                <ActionButton
                  iconProps={signOutIcon}
                  ariaLabel="Sign Out"
                  text="Sign Out"
                  className="mr-3"
                  styles={logoButtonStyles}
                  onClick={(): void => {
                    localStorage.removeItem('UserName');
                    authService?.logout();
                  }}
                />
              </Text>
            </Stack.Item>
          </Stack>
        </Stack>
        {promptMessage && (
          <Dialog
            hidden={!promptMessage}
            dialogContentProps={{
              title: togglingDarkMode ? 'Toggle Dark Mode and exit to Dashboard?' : 'Exit to Dashboard?',
              subText: promptMessage
            }}
            modalProps={{
              overlay: {
                isDarkThemed: true
              }
            }}
          >
            <DialogFooter>
              <SalusPrimaryButton
                onClick={(): void => closeDialog(togglingDarkMode ? toggleDarkMode : returnHome)}
                text="Yes"
              />
              <DefaultButton onClick={(): void => closeDialog()} text="No" />
            </DialogFooter>
          </Dialog>
        )}
      </header>
    </div>
  );
};

export default withRouter(NavBar);
